import type { FieldValues } from 'react-hook-form';
import clsx from 'clsx';
import { ReactElement } from 'react';
import { FormFieldBlock } from './field-block';
import { FormSectionBlock } from './section-block';
import { FormTextContentBlock } from './text-content-block';
import {
  FormBlockConfig,
  FormBlockType,
  FormContentConfig,
  FormFieldConfig,
  FormRowConfig,
  FormSectionConfig,
  FormTextContentConfig,
  HookFormProps,
  isFieldBlock,
} from './types';

const ScheduleFormBlock = <FormValues extends FieldValues>({
  type,
  watch,
  control,
  ...rest
}: (FormContentConfig<FormValues> | FormFieldConfig<FormValues>) &
  HookFormProps<FormValues>): ReactElement => {
  switch (type) {
    case FormBlockType.SECTION: {
      const {
        blockId,
        title,
        variant,
        children: rows,
        isHiddenValidation,
      } = rest as FormSectionConfig<FormValues>;
      return (
        <FormSectionBlock<FormValues>
          watch={watch}
          control={control}
          title={title}
          variant={variant}
          blockId={blockId}
          isHiddenValidation={isHiddenValidation}
        >
          {rows.map((row, i) => {
            // if the row is an array of fields, we render each one in the row with equal size
            if (Array.isArray(row)) {
              return (
                <div
                  className={`flex w-full flex-col gap-4 tablet:flex-row`}
                  key={`${row.blockId ?? row.name}-${i}`}
                >
                  {row.map((subField: FormBlockConfig<FormValues>) => (
                    <div
                      className={'basis-full'}
                      key={subField.name ?? subField.blockId}
                    >
                      {isFieldBlock(row) ? (
                        <FormFieldBlock
                          watch={watch}
                          control={control}
                          field={row}
                        />
                      ) : (
                        <ScheduleFormBlock
                          watch={watch}
                          control={control}
                          {...row}
                        />
                      )}
                    </div>
                  ))}
                </div>
              );
            }
            return isFieldBlock(row) ? (
              <div className={'w-full'} key={row.name}>
                <FormFieldBlock watch={watch} control={control} field={row} />
              </div>
            ) : (
              <ScheduleFormBlock
                watch={watch}
                key={row.blockId}
                control={control}
                {...row}
              />
            );
          })}
        </FormSectionBlock>
      );
    }
    case FormBlockType.ROW: {
      const { children, blockId } = rest as FormRowConfig<FormValues>;
      return (
        <div
          className={`flex w-full flex-col gap-4 tablet:flex-row`}
          key={blockId}
        >
          {children.map((child, i) =>
            isFieldBlock(child) ? (
              <div className={'w-full'} key={`${child.name}-${i}`}>
                <FormFieldBlock watch={watch} control={control} field={child} />
              </div>
            ) : (
              <ScheduleFormBlock
                key={`${child.blockId}-${i}`}
                watch={watch}
                control={control}
                {...child}
              />
            ),
          )}
        </div>
      );
    }
    case FormBlockType.TEXT_CONTENT: {
      const { blockId, content, separator } = rest as FormTextContentConfig;
      return (
        <FormTextContentBlock
          blockId={blockId}
          key={blockId}
          content={content}
          separator={separator}
        />
      );
    }
    default: {
      return (
        <div className={'w-full'} key={rest.name}>
          <FormFieldBlock<FormValues>
            control={control}
            field={rest as FormFieldConfig<FormValues>}
          />
        </div>
      );
    }
  }
};

interface FormProps<FormFields extends FieldValues>
  extends HookFormProps<FormFields> {
  blocks: (FormContentConfig<FormFields> | FormFieldConfig<FormFields>)[];
  className?: string;
}

/**
 * Renders a scheduling form for a given form configuration.
 * The form configuration should be provided as a list of blocks.
 *
 * A section should have the form:
 *  {
 *   blockId?: string; // Unique key for the block if not a field
 *   name?: string; // Unique key for the block if a field
 *   type: FormBlockType; // Type of the block
 *   title?: string; // Title of the section
 *  }
 *
 * @param sections {FormSectionConfig[]} - List of sections to render
 */
export const ScheduleForm = <FormValues extends FieldValues>({
  blocks,
  control,
  className,
  watch,
}: FormProps<FormValues>): ReactElement => {
  return (
    <div
      data-testid={'schedule-form'}
      id={'schedule-form'}
      className={clsx('flex w-full flex-col gap-10', className)}
    >
      {blocks.map((block) => (
        <ScheduleFormBlock<FormValues>
          key={block.name ?? block.blockId}
          control={control}
          watch={watch}
          {...block}
        />
      ))}
    </div>
  );
};
